import React, { useState, useEffect } from "react";
import { Users, MapPin, DollarSign, TrendingUp, Trash2, CheckCircle, AlertCircle, RefreshCw, Mail, Bell, Plus, Edit2, BarChart2, X, Zap, Globe, Tag, Shield } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { INDUSTRY_TYPES } from "@/constants/industries";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  paid: "bg-green-100 text-green-700",
  active: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  expired: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-600",
};

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 100, max_uses: 1, notes: "", expires_at: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCoupons(); }, []);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/coupons`, { headers: authHeaders() });
      setCoupons(res.data.coupons || []);
    } catch {}
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return;
    setSaving(true); setMsg("");
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim().toUpperCase(),
        discount_pct: parseInt(form.discount_pct) || 100,
        max_uses: parseInt(form.max_uses) || 1,
        notes: form.notes,
        expires_at: form.expires_at || null,
      }, { headers: authHeaders() });
      setMsg(`Coupon "${form.code.toUpperCase()}" created!`);
      setForm({ code: "", discount_pct: 100, max_uses: 1, notes: "", expires_at: "" });
      loadCoupons();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Error creating coupon");
    }
    setSaving(false);
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: authHeaders() });
      setCoupons(c => c.filter(x => x.id !== id));
    } catch {}
  };

  return (
    <div data-testid="admin-coupons-tab">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
          <Tag size={16} className="text-violet-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Promo Codes</h2>
          <p className="text-sm text-[#64748B]">Create coupon codes to let advertisers claim ZIP codes for free or at a discount</p>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="card-base mb-6 max-w-2xl">
        <h3 className="font-bold text-[#0F172A] mb-4 flex items-center gap-2"><Plus size={15} /> Create New Promo Code</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">Code *</label>
            <input type="text" required value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              className="input-field font-mono tracking-widest uppercase" placeholder="LAUNCH2027"
              data-testid="coupon-code-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">Discount %</label>
            <select value={form.discount_pct}
              onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))}
              className="input-field" data-testid="coupon-discount-select">
              <option value={100}>100% — Free (recommended)</option>
              <option value={75}>75% OFF</option>
              <option value={50}>50% OFF</option>
              <option value={25}>25% OFF</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">Max Uses (0 = unlimited)</label>
            <input type="number" min="0" value={form.max_uses}
              onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
              className="input-field" data-testid="coupon-max-uses" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">Expires (optional)</label>
            <input type="date" value={form.expires_at}
              onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
              className="input-field" data-testid="coupon-expires" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">Notes (internal only)</label>
            <input type="text" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="input-field" placeholder="e.g. Launch outreach batch — May 2026"
              data-testid="coupon-notes" />
          </div>
        </div>
        {msg && (
          <p className={`text-sm mb-3 font-semibold ${msg.includes("Error") || msg.includes("already") ? "text-red-600" : "text-[#27AE60]"}`}>{msg}</p>
        )}
        <button type="submit" disabled={saving}
          className="btn-action flex items-center gap-2 disabled:opacity-50"
          data-testid="create-coupon-btn">
          <Plus size={15} /> {saving ? "Creating..." : "Create Promo Code"}
        </button>
      </form>

      {/* Coupon list */}
      {loading ? (
        <p className="text-[#64748B]">Loading...</p>
      ) : coupons.length === 0 ? (
        <div className="card-base text-center py-10 text-[#64748B]">
          <Tag size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="font-semibold">No promo codes yet</p>
          <p className="text-sm mt-1">Create one above and share it with your first advertisers.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Code</th>
                <th className="text-left py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Discount</th>
                <th className="text-left py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Used / Limit</th>
                <th className="text-left py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Expires</th>
                <th className="text-left py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => {
                const exhausted = c.max_uses > 0 && c.uses_count >= c.max_uses;
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                const inactive = exhausted || expired;
                return (
                  <tr key={c.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${inactive ? "opacity-60" : ""}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#003366] tracking-widest bg-[#003366]/8 px-2 py-0.5 rounded">{c.code}</span>
                        {inactive && (
                          <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">
                            {expired ? "EXPIRED" : "EXHAUSTED"}
                          </span>
                        )}
                        {!inactive && (
                          <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">ACTIVE</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`font-bold ${c.discount_pct === 100 ? "text-[#27AE60]" : "text-amber-700"}`}>
                        {c.discount_pct === 100 ? "FREE" : `${c.discount_pct}% OFF`}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={exhausted ? "text-red-600 font-semibold" : "text-[#0F172A]"}>
                        {c.uses_count} / {c.max_uses === 0 ? "∞" : c.max_uses}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      {c.expires_at ? (
                        <span className={expired ? "text-red-500 font-semibold" : "text-[#64748B]"}>
                          {new Date(c.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      ) : (
                        <span className="text-[#64748B]">No expiry</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-[#64748B] max-w-xs truncate">{c.notes || "—"}</td>
                    <td className="py-3">
                      <button onClick={() => handleDelete(c.id, c.code)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1"
                        data-testid={`delete-coupon-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [claims, setClaims] = useState([]);
  const [providers, setProviders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const [autoRunning, setAutoRunning] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerEditForm, setBannerEditForm] = useState({});
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerUploadMsg, setBannerUploadMsg] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [pricingForm, setPricingForm] = useState({ single: 150, two: 280, three: 375 });
  const [pricingSaving, setPricingSaving] = useState(false);

  const blankSponsor = {
    zip_code: "", advertiser_type: "tax_pro", business_name: "", contact_name: "",
    phone: "", email: "", city: "", state: "", address: "", website: "", description: "",
    banner_title: "", banner_tagline: "", banner_cta_text: "Learn More", banner_cta_url: "",
    expires_at: "2027-04-15T00:00:00",
  };
  const [addForm, setAddForm] = useState(blankSponsor);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, claimsRes, providersRes, txRes, leadsRes, resRes, sponsorsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/claims`, { headers: authHeaders() }),
        axios.get(`${API}/admin/providers`, { headers: authHeaders() }),
        axios.get(`${API}/admin/transactions`, { headers: authHeaders() }),
        axios.get(`${API}/admin/leads`, { headers: authHeaders() }),
        axios.get(`${API}/admin/reservations`, { headers: authHeaders() }),
        axios.get(`${API}/admin/sponsors`, { headers: authHeaders() }),
        axios.get(`${API}/pricing`),
      ]);
      setStats(statsRes.data);
      setClaims(claimsRes.data.claims || []);
      setProviders(providersRes.data.providers || []);
      setTransactions(txRes.data.transactions || []);
      setLeads(leadsRes.data.leads || []);
      setReservations(resRes.data.reservations || []);
      setSponsors(sponsorsRes.data.sponsors || []);
      const p = pricingRes.data;
      setPricingForm({ single: p.single, two: p.two, three: p.three });
    } catch (e) {
      console.error("Admin load error", e);
    }
    setLoading(false);
  };

  const updateClaimStatus = async (claimId, status) => {
    try {
      await axios.put(`${API}/admin/claims/${claimId}/status`, { status }, { headers: authHeaders() });
      setClaims(claims.map(c => c.id === claimId ? { ...c, payment_status: status } : c));
      setActionMsg(`Updated to ${status}`);
      setTimeout(() => setActionMsg(""), 2500);
    } catch { setActionMsg("Update failed"); }
  };

  const submitAddSponsor = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    try {
      const res = await axios.post(`${API}/admin/add-sponsor`, addForm, { headers: authHeaders() });
      setActionMsg(res.data.message);
      setShowAddForm(false);
      setAddForm(blankSponsor);
      await loadAll();
      setTimeout(() => setActionMsg(""), 6000);
    } catch (err) {
      setActionMsg(err?.response?.data?.detail || "Failed to add sponsor");
    }
    setFormSaving(false);
  };

  const submitEditSponsor = async (e) => {
    e.preventDefault();
    if (!editingSponsor) return;
    setFormSaving(true);
    try {
      await axios.put(`${API}/admin/sponsors/${editingSponsor.id}`, editForm, { headers: authHeaders() });
      setActionMsg("Sponsor updated successfully");
      setEditingSponsor(null);
      await loadAll();
      setTimeout(() => setActionMsg(""), 4000);
    } catch { setActionMsg("Update failed"); }
    setFormSaving(false);
  };

  const openEditSponsor = async (s) => {
    setEditingSponsor(s);
    setEditForm({});
    setShowAddForm(false);
    setEditingBanner(null);
    setBannerEditForm({});
    setBannerUploadMsg("");
    if (s.banner_id) {
      try {
        const res = await axios.get(`${API}/marketplace/banners/${s.banner_id}`, { headers: authHeaders() });
        setEditingBanner(res.data);
        setBannerEditForm({
          title: res.data.title || "",
          tagline: res.data.tagline || "",
          cta_url: res.data.cta_url || "",
          cta_text: res.data.cta_text || "Learn More",
          is_active: res.data.is_active ?? true,
        });
      } catch { setEditingBanner(null); }
    }
  };

  const saveBannerEdits = async () => {
    if (!editingBanner) return;
    setFormSaving(true);
    try {
      await axios.put(`${API}/marketplace/banners/${editingBanner.id}`, bannerEditForm, { headers: authHeaders() });
      setActionMsg("Banner updated successfully");
      setTimeout(() => setActionMsg(""), 3000);
      await loadAll();
    } catch { setActionMsg("Banner update failed"); }
    setFormSaving(false);
  };

  const uploadBannerImage = async (file) => {
    if (!editingBanner || !file) return;
    setBannerUploading(true);
    setBannerUploadMsg("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        `${API}/marketplace/banners/${editingBanner.id}/upload-image`,
        formData,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
      );
      setEditingBanner(b => ({ ...b, image_url: res.data.image_url }));
      setBannerUploadMsg("Image uploaded successfully");
      await loadAll();
    } catch (e) {
      setBannerUploadMsg(e.response?.data?.detail || "Upload failed");
    }
    setBannerUploading(false);
  };

  const runFollowups = async () => {
    setAutoRunning(true);
    try {
      const res = await axios.post(`${API}/admin/run-followups`, {}, { headers: authHeaders() });
      setActionMsg(`Follow-ups sent — F1: ${res.data.follow_up_1_sent}, F2: ${res.data.follow_up_2_sent}`);
      setTimeout(() => setActionMsg(""), 5000);
    } catch { setActionMsg("Follow-up run failed"); }
    setAutoRunning(false);
  };

  const sendRenewals = async (days) => {
    setAutoRunning(true);
    try {
      const res = await axios.post(`${API}/admin/send-renewals?days=${days}`, {}, { headers: authHeaders() });
      setActionMsg(`Renewal emails sent: ${res.data.renewal_emails_sent} (${days}-day window)`);
      setTimeout(() => setActionMsg(""), 5000);
    } catch { setActionMsg("Renewal send failed"); }
    setAutoRunning(false);
  };

  const deleteClaim = async (claimId) => {
    if (!window.confirm("Delete this claim?")) return;
    try {
      await axios.delete(`${API}/admin/claims/${claimId}`, { headers: authHeaders() });
      setClaims(claims.filter(c => c.id !== claimId));
      setActionMsg("Claim deleted");
      setTimeout(() => setActionMsg(""), 2500);
    } catch { setActionMsg("Delete failed"); }
  };

  const statCards = [
    { label: "Tax Pros Registered", value: stats.total_providers || 0, icon: <Users size={22} />, color: "text-[#003366]" },
    { label: "Active ZIP Claims", value: stats.active_claims || 0, icon: <MapPin size={22} />, color: "text-[#2ECC71]" },
    { label: "Pending Payments", value: stats.pending_claims || 0, icon: <AlertCircle size={22} />, color: "text-[#FFD700]" },
    { label: "Total Revenue", value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: <DollarSign size={22} />, color: "text-[#003366]" },
    { label: "Total Leads", value: stats.total_leads || 0, icon: <TrendingUp size={22} />, color: "text-[#2ECC71]" },
    { label: "Paid Transactions", value: stats.paid_transactions || 0, icon: <CheckCircle size={22} />, color: "text-[#003366]" },
  ];

  const tabs = ["overview", "coupons", "pricing", "sponsors", "bulkclaim", "claims", "providers", "transactions", "leads", "automations"];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="bg-[#003366] py-6">
        <div className="section-container flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Admin Dashboard</h1>
            <p className="text-slate-300 text-sm">NextDayRefund.com — 2026/2027 Tax Season</p>
          </div>
          <button onClick={loadAll} className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors" data-testid="admin-refresh-btn">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      <div className="section-container py-6">
        {actionMsg && (
          <div className="mb-4 bg-[#2ECC71]/10 border border-[#2ECC71]/20 text-[#27AE60] text-sm font-semibold px-4 py-2 rounded-lg" data-testid="action-message">
            {actionMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit mb-6 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all whitespace-nowrap capitalize ${tab === t ? "bg-[#003366] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}>
              {t === "bulkclaim" ? "Bulk Claim" : t}
            </button>
          ))}
        </div>

        {loading && <div className="text-center py-10 text-[#64748B]">Loading data...</div>}

        {/* Overview */}
        {!loading && tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {statCards.map((s, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 text-center" data-testid={`stat-card-${i}`}>
                  <div className={`${s.color} flex justify-center mb-2`}>{s.icon}</div>
                  <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-[#64748B] mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Recent Claims */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-bold text-[#0F172A] mb-4">Recent ZIP Code Claims</h3>
              {claims.slice(0, 5).length === 0 ? (
                <p className="text-[#64748B] text-sm">No claims yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100 text-[#64748B] text-left">
                      <th className="pb-2 font-semibold">ZIP</th>
                      <th className="pb-2 font-semibold">Business</th>
                      <th className="pb-2 font-semibold">Status</th>
                      <th className="pb-2 font-semibold">Date</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {claims.slice(0, 5).map(c => (
                        <tr key={c.id} className="py-2">
                          <td className="py-2 font-bold text-[#003366]">{c.zip_code}</td>
                          <td className="py-2 text-[#334155]">{c.business_name}</td>
                          <td className="py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColors[c.payment_status] || statusColors.pending}`}>{c.payment_status}</span></td>
                          <td className="py-2 text-[#64748B]">{new Date(c.claimed_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coupons Tab */}
        {!loading && tab === "coupons" && <CouponsTab />}

        {/* Pricing Tab */}
        {!loading && tab === "pricing" && (
          <div data-testid="admin-pricing-tab" className="max-w-lg">
            <h2 className="text-lg font-bold text-[#0F172A] mb-1">ZIP Claim Pricing</h2>
            <p className="text-[#64748B] text-sm mb-6">Update prices here and they take effect immediately for all new checkouts. Bulk savings are auto-calculated.</p>

            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
              {[
                { key: "single", label: "Single ZIP Price", desc: "Base price per ZIP" },
                { key: "two", label: "2 ZIP Bundle", desc: `Savings: $${Math.max(0, 2 * pricingForm.single - pricingForm.two).toFixed(0)} off vs. 2× single` },
                { key: "three", label: "3 ZIP Bundle", desc: `Savings: $${Math.max(0, 3 * pricingForm.single - pricingForm.three).toFixed(0)} off vs. 3× single` },
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1">{label}</label>
                  <p className="text-xs text-[#64748B] mb-2">{desc}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[#64748B] font-bold">$</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={pricingForm[key]}
                      onChange={e => setPricingForm(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
                      className="input-field w-40"
                      data-testid={`pricing-${key}-input`}
                    />
                  </div>
                </div>
              ))}

              <div className="bg-[#F0FDF4] border border-[#2ECC71]/30 rounded-lg p-4 text-sm">
                <div className="font-semibold text-[#0F172A] mb-1">Preview</div>
                <div className="text-[#334155]">1 ZIP = <strong>${pricingForm.single}</strong></div>
                <div className="text-[#334155]">2 ZIPs = <strong>${pricingForm.two}</strong> {pricingForm.two < 2 * pricingForm.single && <span className="text-[#2ECC71] text-xs">(save ${(2 * pricingForm.single - pricingForm.two).toFixed(0)})</span>}</div>
                <div className="text-[#334155]">3 ZIPs = <strong>${pricingForm.three}</strong> {pricingForm.three < 3 * pricingForm.single && <span className="text-[#2ECC71] text-xs">(save ${(3 * pricingForm.single - pricingForm.three).toFixed(0)})</span>}</div>
              </div>

              <button
                onClick={async () => {
                  setPricingSaving(true);
                  try {
                    await axios.put(`${API}/admin/pricing`, pricingForm, { headers: authHeaders() });
                    setActionMsg(`Pricing updated: $${pricingForm.single} / $${pricingForm.two} / $${pricingForm.three}`);
                    setTimeout(() => setActionMsg(""), 4000);
                  } catch { setActionMsg("Failed to update pricing"); }
                  setPricingSaving(false);
                }}
                disabled={pricingSaving}
                className="btn-action !py-2.5 !px-6 disabled:opacity-50"
                data-testid="save-pricing-btn"
              >
                {pricingSaving ? "Saving..." : "Save Pricing"}
              </button>
            </div>
          </div>
        )}

        {/* Sponsors Tab — God Mode */}
        {!loading && tab === "sponsors" && (
          <div data-testid="admin-sponsors">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">Sponsor Management</h2>
                <p className="text-sm text-[#64748B]">{sponsors.length} active sponsors — add any ZIP + industry, banner auto-created, no payment required</p>
              </div>
              <button
                onClick={() => { setShowAddForm(true); setEditingSponsor(null); }}
                className="btn-action flex items-center gap-2"
                data-testid="add-sponsor-btn"
              >
                <Plus size={16} /> Add Sponsor
              </button>
            </div>

            {actionMsg && (
              <div className="mb-4 px-4 py-3 bg-[#F0FDF4] border border-[#2ECC71]/30 text-[#27AE60] text-sm font-semibold rounded-lg flex items-center gap-2" data-testid="action-msg">
                <CheckCircle size={16} /> {actionMsg}
              </div>
            )}

            {/* Add Sponsor Form */}
            {showAddForm && (
              <div className="bg-white border-2 border-[#003366]/20 rounded-xl p-6 mb-6" data-testid="add-sponsor-form">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-black text-[#003366] text-base flex items-center gap-2">
                    <Plus size={18} /> Add New Sponsor (No Payment)
                  </h3>
                  <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>
                <form onSubmit={submitAddSponsor}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    <div className="md:col-span-3 pb-2 border-b border-slate-100">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#64748B]">ZIP Territory</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">ZIP Code *</label>
                      <input required type="text" maxLength="5" placeholder="e.g. 90210" value={addForm.zip_code} onChange={e => setAddForm(f=>({...f, zip_code: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-zip" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Industry *</label>
                      <select required value={addForm.advertiser_type} onChange={e => setAddForm(f=>({...f, advertiser_type: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-industry">
                        {INDUSTRY_TYPES.filter(t => t.value !== "all").map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Expires At</label>
                      <input type="text" placeholder="2027-04-15T00:00:00" value={addForm.expires_at} onChange={e => setAddForm(f=>({...f, expires_at: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-expires" />
                    </div>

                    <div className="md:col-span-3 pb-2 border-b border-slate-100 mt-2">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#64748B]">Business Info</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Business Name *</label>
                      <input required type="text" placeholder="ABC Tax Services" value={addForm.business_name} onChange={e => setAddForm(f=>({...f, business_name: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-business-name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Contact Name</label>
                      <input type="text" placeholder="John Smith" value={addForm.contact_name} onChange={e => setAddForm(f=>({...f, contact_name: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Email *</label>
                      <input required type="email" placeholder="owner@business.com" value={addForm.email} onChange={e => setAddForm(f=>({...f, email: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-email" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Phone</label>
                      <input type="tel" placeholder="(555) 555-5555" value={addForm.phone} onChange={e => setAddForm(f=>({...f, phone: e.target.value}))} className="input-field !py-2 !text-sm" data-testid="add-phone" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Website</label>
                      <input type="text" placeholder="https://..." value={addForm.website} onChange={e => setAddForm(f=>({...f, website: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">City</label>
                      <input type="text" placeholder="Miami" value={addForm.city} onChange={e => setAddForm(f=>({...f, city: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">State</label>
                      <input type="text" maxLength="2" placeholder="FL" value={addForm.state} onChange={e => setAddForm(f=>({...f, state: e.target.value.toUpperCase()}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Description (shown on listing)</label>
                      <textarea rows={2} placeholder="Brief description of services..." value={addForm.description} onChange={e => setAddForm(f=>({...f, description: e.target.value}))} className="input-field !py-2 !text-sm resize-none" />
                    </div>

                    <div className="md:col-span-3 pb-2 border-b border-slate-100 mt-2">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#64748B]">Banner Ad (Auto-Created)</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Banner Title</label>
                      <input type="text" placeholder="Auto-filled from business name" value={addForm.banner_title} onChange={e => setAddForm(f=>({...f, banner_title: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">CTA Button Text</label>
                      <input type="text" placeholder="Learn More" value={addForm.banner_cta_text} onChange={e => setAddForm(f=>({...f, banner_cta_text: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Banner Tagline</label>
                      <input type="text" placeholder="Auto-filled from ZIP/city" value={addForm.banner_tagline} onChange={e => setAddForm(f=>({...f, banner_tagline: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">CTA URL</label>
                      <input type="text" placeholder="https://..." value={addForm.banner_cta_url} onChange={e => setAddForm(f=>({...f, banner_cta_url: e.target.value}))} className="input-field !py-2 !text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" disabled={formSaving} className="btn-action flex items-center gap-2 disabled:opacity-60" data-testid="submit-add-sponsor">
                      <Plus size={16} /> {formSaving ? "Adding..." : "Add Sponsor — No Payment"}
                    </button>
                    <button type="button" onClick={() => setShowAddForm(false)} className="btn-outline">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Edit Sponsor Modal */}
            {editingSponsor && (
              <div className="bg-white border-2 border-amber-300 rounded-xl p-6 mb-6" data-testid="edit-sponsor-form">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-black text-[#0F172A] text-base flex items-center gap-2">
                    <Edit2 size={18} /> Edit: {editingSponsor.business_name}
                  </h3>
                  <button onClick={() => { setEditingSponsor(null); setEditingBanner(null); }} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>

                {/* Claim fields */}
                <form onSubmit={submitEditSponsor}>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Claim Info</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    {[
                      { key: "business_name", label: "Business Name", span: 2 },
                      { key: "contact_name", label: "Contact Name" },
                      { key: "email", label: "Email", type: "email" },
                      { key: "phone", label: "Phone" },
                      { key: "website", label: "Website" },
                      { key: "city", label: "City" },
                      { key: "state", label: "State" },
                    ].map(({ key, label, type, span }) => (
                      <div key={key} className={span ? `md:col-span-${span}` : ""}>
                        <label className="text-xs font-semibold text-[#64748B] block mb-1">{label}</label>
                        <input type={type || "text"} defaultValue={editingSponsor[key] || ""} onChange={e => setEditForm(f=>({...f, [key]: e.target.value}))} className="input-field !py-2 !text-sm" />
                      </div>
                    ))}
                    <div className="md:col-span-3">
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Description</label>
                      <textarea rows={2} defaultValue={editingSponsor.description || ""} onChange={e => setEditForm(f=>({...f, description: e.target.value}))} className="input-field !py-2 !text-sm resize-none" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#64748B] block mb-1">Status</label>
                      <select defaultValue={editingSponsor.payment_status} onChange={e => setEditForm(f=>({...f, payment_status: e.target.value}))} className="input-field !py-2 !text-sm">
                        {["active", "paid", "pending", "expired", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-6">
                    <button type="submit" disabled={formSaving} className="btn-action flex items-center gap-2 disabled:opacity-60" data-testid="submit-edit-sponsor">
                      <CheckCircle size={16} /> {formSaving ? "Saving..." : "Save Claim"}
                    </button>
                    <button type="button" onClick={() => { setEditingSponsor(null); setEditingBanner(null); }} className="btn-outline">Cancel</button>
                  </div>
                </form>

                {/* Banner section */}
                <div className="border-t border-slate-200 pt-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Banner Ad</p>
                  {!editingBanner ? (
                    <p className="text-sm text-slate-400">No banner linked to this sponsor yet.</p>
                  ) : (
                    <div className="space-y-4">

                      {/* Image upload */}
                      <div>
                        <label className="text-xs font-semibold text-[#64748B] block mb-2">Banner Image</label>
                        <div className="flex items-center gap-4 flex-wrap">
                          {/* Preview */}
                          <div className="w-24 h-16 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
                            style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>
                            {editingBanner.image_url
                              ? <img src={editingBanner.image_url} alt="Banner" className="w-full h-full object-cover" />
                              : <span className="text-slate-400 text-xs text-center px-1">No image</span>}
                          </div>
                          <div>
                            <label className="btn-action cursor-pointer flex items-center gap-2 !py-2 !px-4 !text-xs"
                              data-testid="admin-banner-upload-btn">
                              {bannerUploading ? "Uploading..." : "Upload Image"}
                              <input type="file" accept="image/*" className="hidden"
                                onChange={e => e.target.files?.[0] && uploadBannerImage(e.target.files[0])}
                                disabled={bannerUploading} />
                            </label>
                            <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, GIF · max 5MB</p>
                            {bannerUploadMsg && (
                              <p className={`text-xs mt-1 font-semibold ${bannerUploadMsg.includes("success") ? "text-[#2ECC71]" : "text-red-500"}`}>
                                {bannerUploadMsg}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Banner text fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <label className="text-xs font-semibold text-[#64748B] block mb-1">Banner Title</label>
                          <input value={bannerEditForm.title || ""} onChange={e => setBannerEditForm(f=>({...f, title: e.target.value}))}
                            className="input-field !py-2 !text-sm" placeholder="e.g. Miami's #1 Debt Relief Firm"
                            data-testid="admin-banner-title" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs font-semibold text-[#64748B] block mb-1">Tagline</label>
                          <input value={bannerEditForm.tagline || ""} onChange={e => setBannerEditForm(f=>({...f, tagline: e.target.value}))}
                            className="input-field !py-2 !text-sm" placeholder="e.g. Free consultation · No upfront fees"
                            data-testid="admin-banner-tagline" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-[#64748B] block mb-1">CTA Button URL</label>
                          <input value={bannerEditForm.cta_url || ""} onChange={e => setBannerEditForm(f=>({...f, cta_url: e.target.value}))}
                            className="input-field !py-2 !text-sm" placeholder="https://..."
                            data-testid="admin-banner-cta-url" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-[#64748B] block mb-1">CTA Button Text</label>
                          <input value={bannerEditForm.cta_text || ""} onChange={e => setBannerEditForm(f=>({...f, cta_text: e.target.value}))}
                            className="input-field !py-2 !text-sm" placeholder="Get Free Quote"
                            data-testid="admin-banner-cta-text" />
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                          <label className="text-xs font-semibold text-[#64748B]">Banner Active</label>
                          <button type="button"
                            onClick={() => setBannerEditForm(f => ({...f, is_active: !f.is_active}))}
                            className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${bannerEditForm.is_active ? "bg-[#2ECC71]" : "bg-slate-300"}`}
                            data-testid="admin-banner-toggle">
                            <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${bannerEditForm.is_active ? "translate-x-5" : "translate-x-0.5"}`} />
                          </button>
                          <span className={`text-xs font-semibold ${bannerEditForm.is_active ? "text-[#2ECC71]" : "text-slate-400"}`}>
                            {bannerEditForm.is_active ? "Visible" : "Hidden"}
                          </span>
                        </div>
                      </div>

                      <button onClick={saveBannerEdits} disabled={formSaving}
                        className="btn-action flex items-center gap-2 !text-sm disabled:opacity-60"
                        data-testid="save-banner-btn">
                        <CheckCircle size={15} /> {formSaving ? "Saving..." : "Save Banner Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sponsors Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">ZIP</th>
                      <th className="px-4 py-3 font-semibold">Industry</th>
                      <th className="px-4 py-3 font-semibold">Business</th>
                      <th className="px-4 py-3 font-semibold">Contact</th>
                      <th className="px-4 py-3 font-semibold">Banner</th>
                      <th className="px-4 py-3 font-semibold">Clicks</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {sponsors.map(s => (
                      <tr key={s.id} className="hover:bg-[#F8FAFC]" data-testid={`sponsor-row-${s.id}`}>
                        <td className="px-4 py-3 font-black text-[#003366]">{s.zip_code}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-semibold bg-[#003366]/10 text-[#003366] px-2 py-0.5 rounded-full">
                            {INDUSTRY_TYPES.find(t => t.value === s.advertiser_type)?.label || s.advertiser_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#0F172A]">{s.business_name}</div>
                          {s.city && <div className="text-xs text-[#64748B]">{s.city}, {s.state}</div>}
                        </td>
                        <td className="px-4 py-3 text-[#334155] text-xs">
                          <div>{s.email}</div>
                          {s.phone && <div className="text-[#64748B]">{s.phone}</div>}
                        </td>
                        <td className="px-4 py-3">
                          {s.has_banner
                            ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.banner_active ? "bg-[#2ECC71]/15 text-[#27AE60]" : "bg-slate-100 text-slate-500"}`}>
                                {s.banner_active ? "Active" : "Paused"}
                              </span>
                            : <span className="text-xs text-slate-400">No banner</span>
                          }
                        </td>
                        <td className="px-4 py-3 font-bold text-[#003366]">
                          <span className="flex items-center gap-1"><BarChart2 size={12} /> {s.banner_clicks}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[s.payment_status] || statusColors.pending}`}>
                            {s.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex items-center gap-2">
                          <button
                            onClick={() => openEditSponsor(s)}
                            className="p-1.5 text-[#003366] hover:bg-[#003366]/10 rounded transition-colors"
                            title="Edit sponsor"
                            data-testid={`edit-sponsor-${s.id}`}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => deleteClaim(s.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete sponsor"
                            data-testid={`delete-sponsor-${s.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sponsors.length === 0 && (
                  <div className="text-center py-12 text-[#64748B]">
                    <MapPin size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">No sponsors yet</p>
                    <p className="text-sm mt-1">Click "Add Sponsor" to add your first one — no payment required</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Claim Tab */}
        {!loading && tab === "bulkclaim" && <BulkClaimTab />}

        {/* Claims Tab */}
        {!loading && tab === "claims" && (
          <div data-testid="admin-claims">
            <h2 className="text-lg font-bold text-[#0F172A] mb-4">All ZIP Code Claims ({claims.length})</h2>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">ZIP</th>
                      <th className="px-4 py-3 font-semibold">Business</th>
                      <th className="px-4 py-3 font-semibold">Contact</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {claims.map(c => (
                      <tr key={c.id} className="hover:bg-[#F8FAFC] transition-colors" data-testid={`admin-claim-row-${c.id}`}>
                        <td className="px-4 py-3 font-bold text-[#003366]">{c.zip_code}</td>
                        <td className="px-4 py-3 text-[#334155]">{c.business_name}<br/><span className="text-xs text-[#64748B]">{c.city}, {c.state}</span></td>
                        <td className="px-4 py-3 text-[#334155]">{c.email}</td>
                        <td className="px-4 py-3">
                          <select
                            value={c.payment_status}
                            onChange={e => updateClaimStatus(c.id, e.target.value)}
                            className={`text-xs font-semibold px-2 py-1 rounded border-0 outline-none cursor-pointer ${statusColors[c.payment_status] || statusColors.pending}`}
                            data-testid={`claim-status-select-${c.id}`}
                          >
                            {["paid", "active", "pending", "expired", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">{new Date(c.claimed_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteClaim(c.id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-claim-btn-${c.id}`}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {claims.length === 0 && <div className="text-center py-8 text-[#64748B]">No claims yet.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Providers Tab */}
        {!loading && tab === "providers" && (
          <div data-testid="admin-providers">
            <h2 className="text-lg font-bold text-[#0F172A] mb-4">All Tax Pros ({providers.length})</h2>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">Business</th>
                      <th className="px-4 py-3 font-semibold">Registered</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {providers.map(p => (
                      <tr key={p.id} className="hover:bg-[#F8FAFC]" data-testid={`provider-row-${p.id}`}>
                        <td className="px-4 py-3 font-semibold text-[#0F172A]">{p.name}</td>
                        <td className="px-4 py-3 text-[#334155]">{p.email}</td>
                        <td className="px-4 py-3 text-[#334155]">{p.business_name || "—"}</td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {providers.length === 0 && <div className="text-center py-8 text-[#64748B]">No providers yet.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {!loading && tab === "transactions" && (
          <div data-testid="admin-transactions">
            <h2 className="text-lg font-bold text-[#0F172A] mb-4">All Transactions ({transactions.length})</h2>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">Session ID</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">ZIP</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {transactions.map(t => (
                      <tr key={t.id} className="hover:bg-[#F8FAFC]" data-testid={`tx-row-${t.id}`}>
                        <td className="px-4 py-3 text-xs text-[#64748B] max-w-[120px] truncate">{t.session_id}</td>
                        <td className="px-4 py-3 text-[#334155]">{t.email}</td>
                        <td className="px-4 py-3 font-bold text-[#003366]">{t.zip_code}</td>
                        <td className="px-4 py-3 font-semibold text-[#003366]">${t.amount}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColors[t.payment_status] || statusColors.pending}`}>{t.payment_status}</span></td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && <div className="text-center py-8 text-[#64748B]">No transactions yet.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {!loading && tab === "leads" && (
          <div data-testid="admin-leads">
            <h2 className="text-lg font-bold text-[#0F172A] mb-4">All Leads ({leads.length})</h2>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">ZIP</th>
                      <th className="px-4 py-3 font-semibold">Phone</th>
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">Message</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {leads.map(l => (
                      <tr key={l.id} className="hover:bg-[#F8FAFC]" data-testid={`lead-row-${l.id}`}>
                        <td className="px-4 py-3 font-semibold text-[#0F172A]">{l.taxpayer_name || "Anonymous"}</td>
                        <td className="px-4 py-3 font-bold text-[#003366]">{l.zip_code}</td>
                        <td className="px-4 py-3 text-[#334155]">{l.taxpayer_phone || "—"}</td>
                        <td className="px-4 py-3 text-[#334155]">{l.taxpayer_email || "—"}</td>
                        <td className="px-4 py-3 text-[#64748B] text-xs max-w-[150px] truncate">{l.message || "—"}</td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {leads.length === 0 && <div className="text-center py-8 text-[#64748B]">No leads yet.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Automations Tab */}
        {!loading && tab === "automations" && (
          <div data-testid="admin-automations">
            <h2 className="text-lg font-bold text-[#0F172A] mb-6">Email Automations</h2>

            {/* Abandoned Checkout Recovery */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#003366]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A]">Abandoned Checkout Recovery</h3>
                  <p className="text-sm text-[#64748B] mt-0.5">Send follow-up emails to visitors who reserved a ZIP but didn't complete payment. Runs in two waves: 1 hour after reservation, then 24 hours after.</p>
                </div>
              </div>
              <div className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-4 mb-4">
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">ZIP Reservations (not yet converted)</div>
                  <div className="text-2xl font-black text-[#003366]">{reservations.filter(r => !r.converted).length}</div>
                  <div className="text-xs text-[#64748B]">of {reservations.length} total reservations</div>
                </div>
                <div className="text-right text-xs text-[#64748B] space-y-1">
                  <div>F1 pending: {reservations.filter(r => !r.converted && !r.follow_up_1_sent).length}</div>
                  <div>F2 pending: {reservations.filter(r => !r.converted && r.follow_up_1_sent && !r.follow_up_2_sent).length}</div>
                  <div>Converted: {reservations.filter(r => r.converted).length}</div>
                </div>
              </div>
              <button
                onClick={runFollowups}
                disabled={autoRunning}
                className="btn-action flex items-center gap-2 disabled:opacity-60"
                data-testid="run-followups-btn"
              >
                <Mail size={16} />
                {autoRunning ? "Running..." : "Run Follow-up Emails Now"}
              </button>
              <p className="text-xs text-[#64748B] mt-2">Tip: Set up a daily cron job to call <code className="bg-slate-100 px-1 rounded">POST /api/admin/run-followups</code> automatically.</p>
            </div>

            {/* Renewal Campaign */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#2ECC71]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell size={20} className="text-[#27AE60]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A]">Renewal Campaign</h3>
                  <p className="text-sm text-[#64748B] mt-0.5">Send renewal reminder emails to active advertisers whose ZIP territories are expiring soon. Each tier can only be sent once per claim.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3" data-testid="renewal-buttons">
                {[
                  { days: 60, label: "60-Day Reminder", color: "bg-[#003366] text-white hover:bg-[#002244]" },
                  { days: 30, label: "30-Day Reminder", color: "bg-amber-500 text-white hover:bg-amber-600" },
                  { days: 7, label: "7-Day URGENT", color: "bg-red-600 text-white hover:bg-red-700" },
                ].map(({ days, label, color }) => (
                  <button
                    key={days}
                    onClick={() => sendRenewals(days)}
                    disabled={autoRunning}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-60 ${color}`}
                    data-testid={`renewal-btn-${days}`}
                  >
                    <Bell size={14} /> {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#64748B] mt-3">Each reminder is only sent once per claim. Safe to run multiple times.</p>
            </div>

            {/* ZIP Reservations Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="font-bold text-[#0F172A]">ZIP Reservations Log ({reservations.length})</h3>
                <p className="text-xs text-[#64748B] mt-0.5">Visitors who held a ZIP but haven't paid yet</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-slate-200">
                    <tr className="text-[#64748B] text-left">
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold">ZIP</th>
                      <th className="px-4 py-3 font-semibold">Industry</th>
                      <th className="px-4 py-3 font-semibold">F1</th>
                      <th className="px-4 py-3 font-semibold">F2</th>
                      <th className="px-4 py-3 font-semibold">Converted</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {reservations.map(r => (
                      <tr key={r.id} className="hover:bg-[#F8FAFC]" data-testid={`reservation-row-${r.id}`}>
                        <td className="px-4 py-3 text-[#334155]">{r.email}</td>
                        <td className="px-4 py-3 font-bold text-[#003366]">{r.zip_code}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{r.advertiser_type}</td>
                        <td className="px-4 py-3">{r.follow_up_1_sent ? <CheckCircle size={14} className="text-[#27AE60]" /> : <span className="text-slate-300 text-xs">—</span>}</td>
                        <td className="px-4 py-3">{r.follow_up_2_sent ? <CheckCircle size={14} className="text-[#27AE60]" /> : <span className="text-slate-300 text-xs">—</span>}</td>
                        <td className="px-4 py-3">{r.converted ? <span className="text-xs font-bold text-[#27AE60] bg-[#2ECC71]/10 px-2 py-0.5 rounded-full">PAID</span> : <span className="text-xs text-slate-400">pending</span>}</td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">{new Date(r.reserved_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reservations.length === 0 && <div className="text-center py-8 text-[#64748B]">No reservations yet.</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Bulk Claim Tab ───────────────────────────────────────────────────────────

const US_STATES = [
  { code: "all", name: "All States" },
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function BulkClaimTab() {
  const [mode, setMode] = useState("state"); // "state" | "ziplist"
  const [selectedStates, setSelectedStates] = useState(["VA", "MD", "SC", "FL"]);
  const [zipText, setZipText] = useState("");
  const [selectedTypes, setSelectedTypes] = useState(["all"]);
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [ctaText, setCtaText] = useState("Learn More");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");

  const allIndustries = INDUSTRY_TYPES.filter(i => i.value !== "all");

  const toggleType = (val) => {
    if (val === "all") { setSelectedTypes(["all"]); return; }
    setSelectedTypes(prev => {
      const without = prev.filter(t => t !== "all");
      return without.includes(val) ? without.filter(t => t !== val) : [...without, val];
    });
  };

  const toggleState = (code) => {
    setSelectedStates(prev =>
      prev.includes(code) ? prev.filter(s => s !== code) : [...prev, code]
    );
  };

  const buildPayload = (stateCode, dry_run) => {
    const payload = {
      state: stateCode,
      advertiser_types: selectedTypes,
      business_name: businessName,
      contact_name: contactName,
      email,
      affiliate_url: affiliateUrl,
      banner_cta_text: ctaText,
      dry_run,
    };
    if (mode === "ziplist") {
      payload.zip_list = zipText.split(/[\n,\s]+/).map(z => z.trim()).filter(z => z.length === 5 && /^\d+$/.test(z));
    }
    return payload;
  };

  const handlePreview = async () => {
    setError(""); setPreview(null);
    if (!businessName || !email || !affiliateUrl) { setError("Business name, email and affiliate URL are required."); return; }
    if (mode === "state" && selectedStates.length === 0) { setError("Select at least one state."); return; }
    if (mode === "ziplist" && !zipText.trim()) { setError("Paste at least one ZIP code."); return; }
    setLoading(true);
    try {
      const statesToRun = mode === "state" ? selectedStates : ["manual"];
      let totalZip = 0, totalCreate = 0, totalSkip = 0, totalCombo = 0, industryCount = 0;
      for (const st of statesToRun) {
        const { data } = await axios.post(`${API}/admin/bulk-claim`, buildPayload(st, true), { headers: authHeaders() });
        totalZip += data.zip_count || 0;
        totalCreate += data.would_create || 0;
        totalSkip += data.would_skip || 0;
        totalCombo += data.total_combinations || 0;
        industryCount = data.industry_count || industryCount;
      }
      setPreview({ zip_count: totalZip, would_create: totalCreate, would_skip: totalSkip, total_combinations: totalCombo, industry_count: industryCount });
    } catch (e) {
      setError(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const handleClaim = async () => {
    setError(""); setResult(null);
    setLoading(true);
    try {
      const statesToRun = mode === "state" ? selectedStates : ["manual"];
      let totalCreated = 0, totalSkipped = 0;
      for (let i = 0; i < statesToRun.length; i++) {
        setProgress(`Processing ${statesToRun[i]} (${i + 1}/${statesToRun.length})...`);
        const { data } = await axios.post(`${API}/admin/bulk-claim`, buildPayload(statesToRun[i], false), { headers: authHeaders() });
        totalCreated += data.created || 0;
        totalSkipped += data.skipped || 0;
      }
      setResult({ created: totalCreated, skipped: totalSkipped });
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Bulk claim failed");
    } finally { setLoading(false); setProgress(""); }
  };

  const inputCls = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#003366]/30";

  return (
    <div data-testid="admin-bulkclaim" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#003366] flex items-center justify-center">
          <Zap size={18} className="text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Bulk Admin ZIP Claiming</h2>
          <p className="text-slate-500 text-sm">Mass-claim ZIP codes across industries with your affiliate link — no payment required.</p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setMode("state")} data-testid="bulk-mode-state"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "state" ? "bg-[#003366] text-white" : "bg-white border border-slate-200 text-[#64748B]"}`}>
          By State
        </button>
        <button onClick={() => setMode("ziplist")} data-testid="bulk-mode-ziplist"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "ziplist" ? "bg-[#003366] text-white" : "bg-white border border-slate-200 text-[#64748B]"}`}>
          By ZIP List
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        {/* ZIP source */}
        {mode === "state" ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                States — {selectedStates.length} selected
              </label>
              <div className="flex gap-2 text-xs">
                <button onClick={() => setSelectedStates(US_STATES.map(s => s.code))}
                  className="text-[#003366] hover:underline font-semibold">All 50</button>
                <button onClick={() => setSelectedStates(["VA","MD","SC","FL","GA","NC","TX","CA","NY","FL"])}
                  className="text-[#003366] hover:underline font-semibold">Southeast+</button>
                <button onClick={() => setSelectedStates([])}
                  className="text-red-400 hover:underline font-semibold">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-8 sm:grid-cols-10 gap-1.5 max-h-48 overflow-y-auto border border-slate-100 rounded-lg p-2">
              {US_STATES.map(s => (
                <button key={s.code} onClick={() => toggleState(s.code)} data-testid={`state-toggle-${s.code}`}
                  title={s.name}
                  className={`px-1 py-1 rounded text-xs font-bold transition-all ${selectedStates.includes(s.code) ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {s.code}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">Selected: {selectedStates.length === 0 ? "None" : selectedStates.join(", ")}</p>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">ZIP Codes (one per line or comma-separated, max 5,000)</label>
            <textarea value={zipText} onChange={e => setZipText(e.target.value)} rows={6} placeholder={"33101\n90210\n10001"} className={inputCls} data-testid="bulk-zip-textarea" />
          </div>
        )}

        {/* Industries */}
        <div>
          <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2">Industries</label>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => toggleType("all")} data-testid="bulk-type-all"
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${selectedTypes.includes("all") ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-500"}`}>
              All 12 Industries
            </button>
            {allIndustries.map(ind => (
              <button key={ind.value} onClick={() => toggleType(ind.value)} data-testid={`bulk-type-${ind.value}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${!selectedTypes.includes("all") && selectedTypes.includes(ind.value) ? "text-white" : "bg-slate-100 text-slate-500"}`}
                style={!selectedTypes.includes("all") && selectedTypes.includes(ind.value) ? { background: ind.color } : {}}>
                {ind.label}
              </button>
            ))}
          </div>
        </div>

        {/* Business details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">Business Name</label>
            <input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="NextDayRefund.com" className={inputCls} data-testid="bulk-business-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">Contact Name</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Your Name" className={inputCls} data-testid="bulk-contact-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">Account Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="support@nextdayrefund.com" className={inputCls} data-testid="bulk-email" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">Banner CTA Text</label>
            <input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Learn More" className={inputCls} data-testid="bulk-cta-text" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5">Affiliate / Destination URL</label>
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#003366]/30">
            <span className="pl-3 text-slate-400"><Globe size={15} /></span>
            <input value={affiliateUrl} onChange={e => setAffiliateUrl(e.target.value)} placeholder="https://your-affiliate-link.com/track?ref=ndr" className="flex-1 px-2 py-2 text-sm focus:outline-none" data-testid="bulk-affiliate-url" />
          </div>
          <p className="text-xs text-slate-400 mt-1">This URL is inserted as the CTA link on every banner ad created.</p>
        </div>

        {/* Error */}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}

        {/* Progress */}
        {loading && progress && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            {progress}
          </div>
        )}

        {/* Preview result */}
        {preview && (
          <div className="bg-[#003366]/5 border border-[#003366]/20 rounded-xl p-4" data-testid="bulk-preview-result">
            <p className="text-sm font-bold text-[#003366] mb-1">Preview — {mode === "state" ? selectedStates.length : 1} state{selectedStates.length !== 1 ? "s" : ""} selected (nothing created yet)</p>
            <div className="grid grid-cols-3 gap-3 text-center mt-3">
              <div><div className="text-2xl font-black text-[#2ECC71]">{preview.zip_count?.toLocaleString()}</div><div className="text-xs text-slate-500">ZIPs Found</div></div>
              <div><div className="text-2xl font-black text-[#003366]">{preview.would_create?.toLocaleString()}</div><div className="text-xs text-slate-500">Would Create</div></div>
              <div><div className="text-2xl font-black text-slate-400">{preview.would_skip?.toLocaleString()}</div><div className="text-xs text-slate-500">Already Taken</div></div>
            </div>
            <p className="text-xs text-slate-500 text-center mt-2">{preview.industry_count} industri{preview.industry_count === 1 ? "y" : "es"} × {preview.zip_count?.toLocaleString()} ZIPs = {preview.total_combinations?.toLocaleString()} total slots</p>
            <button onClick={handleClaim} disabled={loading || preview.would_create === 0}
              className="mt-4 w-full bg-[#003366] hover:bg-[#002244] disabled:opacity-50 text-white font-bold py-2.5 rounded-lg transition-colors text-sm"
              data-testid="bulk-confirm-claim">
              {loading ? progress || "Claiming..." : `Confirm — Create ${preview.would_create?.toLocaleString()} Claims`}
            </button>
          </div>
        )}

        {/* Success result */}
        {result && (
          <div className="bg-[#2ECC71]/10 border border-[#2ECC71]/30 rounded-xl p-4" data-testid="bulk-success-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={18} className="text-[#27AE60]" />
              <p className="text-sm font-bold text-[#27AE60]">Bulk Claim Complete!</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-center mt-2">
              <div><div className="text-3xl font-black text-[#2ECC71]">{result.created?.toLocaleString()}</div><div className="text-xs text-slate-500">Claims Created</div></div>
              <div><div className="text-3xl font-black text-slate-400">{result.skipped?.toLocaleString()}</div><div className="text-xs text-slate-500">Already Taken</div></div>
            </div>
            <button onClick={() => setResult(null)} className="mt-4 text-xs text-slate-500 underline w-full text-center">Claim More</button>
          </div>
        )}

        {/* Actions */}
        {!preview && !result && (
          <div className="flex gap-3 pt-1">
            <button onClick={handlePreview} disabled={loading} data-testid="bulk-preview-btn"
              className="flex-1 border-2 border-[#003366] text-[#003366] hover:bg-[#003366]/5 font-bold py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50">
              {loading ? "Loading..." : "Preview Count"}
            </button>
            <button onClick={handleClaim} disabled={loading} data-testid="bulk-claim-btn"
              className="flex-1 bg-[#003366] hover:bg-[#002244] text-white font-bold py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50">
              {loading ? progress || "Claiming..." : "Claim Now (Skip Preview)"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
